import { AGENT_IDS, DATA_CATALOG_API_TOOL_IDS } from '@/app/data-catalog/_common/constants'
import { useApiRequest } from '@/lib/http-utils'
import { useQuery } from '@tanstack/react-query'
import { taskManagerApiPaths } from './_paths'

export type TableMetadataVHField = {
  field_name: string
  change_type: 'added' | 'updated' | 'deleted'
}

export type TableMetadataVH = {
  version: number
  timestamp_unix: number
  timestamp_readable: string
  changed_by: string[]
  change_description: {
    fields_added: TableMetadataVHField[]
    fields_updated: TableMetadataVHField[]
    fields_deleted: TableMetadataVHField[]
    previous_version: string
  }
}

export type TableMetadataVHAPIResponse = {
  service_name: string
  database_name: string
  schema_name: string
  table_name: string
  table_fqn: string
  status: string
  approved_by: string
  current_version: string
  version_history: TableMetadataVH[]
}

export type TableMetadataVHQueryParams = {
  service_name: string
  database_name: string
  schema_name: string
  table_name: string
}

export function useTableMetadataVersionHistoryQuery(params: TableMetadataVHQueryParams) {
  const agentId = AGENT_IDS.DATA_CATALOG_AGENT
  const { apiRequest, domainId } = useApiRequest()
  const catalogSelector = {
    service_name: params.service_name,
    database_name: params.database_name,
    schema_name: params.schema_name,
    table_name: params.table_name,
  }

  return useQuery<TableMetadataVHAPIResponse>({
    queryKey: ['metadata-table-version-history', catalogSelector],
    queryFn: async () => {
      const basePath = taskManagerApiPaths.agentById({ domainId, agentId })

      const response = await apiRequest({
        path: basePath,
        queryParams: { tool_id: DATA_CATALOG_API_TOOL_IDS.GET_TABLE_VERSION_HISTORY },
        options: {
          method: 'POST',
          body: JSON.stringify({ catalogSelector }),
        },
      })

      if (!response.ok) {
        throw new Error('Error fetching table version history.')
      }

      const json = (await response.json()) as { output: TableMetadataVHAPIResponse }
      return json.output
    },
    refetchInterval: (query) => {
      if (query.state.error) return 5000
      return false
    },
  })
}
